<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '../composables/useNotifications.js';
import { useAuth } from '../composables/useAuth.js';
import { usePlayers } from '../composables/usePlayers.js';
import { fmtDateTime } from '../domain/format.js';
import { tabForNotificationType, notificationTextKey } from '../domain/notifications.js';

const { t } = useI18n();
const {
  unreadItems, unreadCount, isRead, markRead, toggleRead, markAllRead,
  pushAvailable, pushActive, start, stop, enablePush,
} = useNotifications();
const { user } = useAuth();
/* Umbenannt, weil `open` hier schon der lokale Auf/Zu-Zustand der Glocke
   ist. */
const { players, open: openPlayerAt } = usePlayers();

const open = ref(false);
const pushError = ref(null);
const root = ref(null);

/* Text der Meldung. Neue Typen tragen keinen fertigen Text in der Datenbank:
   Der Titel ist der Spielername, der Text kommt aus den Sprachdateien in der
   Sprache des Betrachters. Alte Zeilen (player_update u. a.) bringen title und
   body selbst mit und werden unverändert angezeigt. */
function bodyOf(n) {
  const key = notificationTextKey(n.type);
  return key ? t(key) : n.body;
}

async function onEnablePush() {
  pushError.value = await enablePush(user.value?.id);
}

/* Glocke auf/zu. Schließen ohne eine bestimmte Meldung anzuklicken (per
   Glocke selbst oder per onDocumentClick unten) gilt als "alles gesehen" —
   alle sichtbaren gelten als gelesen. Bewusst NICHT über einen watch auf
   `open`, der bei jedem Weg zum Schließen gleich reagieren würde: Eine
   einzelne Meldung anzuklicken schließt die Glocke ebenfalls, soll dabei
   aber nur diese eine als gelesen markieren, siehe openFromNotification()
   unten (TODO "Meldungen: Gelesen-Umfang und Verschwinden nachbessern"). */
function toggleBell() {
  const wasOpen = open.value;
  open.value = !wasOpen;
  if (wasOpen) markAllRead();
}

/* Klick außerhalb schließt das Panel — wie im Original. */
function onDocumentClick(e) {
  if (open.value && root.value && !root.value.contains(e.target)) {
    open.value = false;
    markAllRead();
  }
}

/* Meldung anklicken öffnet den betroffenen Spieler auf dem passenden
   Reiter und markiert NUR diese eine Meldung als gelesen — nicht alle
   gerade sichtbaren. Existiert der Spieler nicht mehr oder sieht die Rolle
   ihn nicht (beides läuft über dieselbe Prüfung: players enthält ohnehin
   nur, was RLS für diese Rolle freigibt) — Meldung bleibt stehen, nichts
   öffnet sich. */
function openFromNotification(n) {
  if (!n.player_id) return;
  const player = players.value.find((p) => p.id === n.player_id);
  if (!player) return;
  markRead(n.id);
  openPlayerAt(player.id, tabForNotificationType(n.type));
  open.value = false;
}

/* Konto gewechselt, ohne dass die Glocke neu eingehängt wurde (z. B. Sitzung
   in einem anderen Tab ersetzt): Lesestatus und Liste des neuen Kontos laden. */
watch(() => user.value?.id, (id) => {
  if (id) start(id);
  else stop();
});

onMounted(() => {
  start(user.value?.id);
  document.addEventListener('click', onDocumentClick);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  stop();
});
</script>

<template>
  <div ref="root" class="notif-root">
    <button class="notif-bell" :title="$t('notifications.title')" @click="toggleBell">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.7 21a2 2 0 0 1-3.4 0" />
      </svg>
      <span v-if="unreadCount > 0" class="notif-badge">{{ unreadCount }}</span>
    </button>

    <div class="notif-panel" :class="{ open }">
      <div class="notif-panel-head">
        <span>{{ $t('notifications.title') }}</span>
        <span v-if="unreadCount > 0" class="unread-hint">
          {{ $t('notifications.unreadCount', unreadCount, { count: unreadCount }) }}
        </span>
      </div>

      <button v-if="pushAvailable && !pushActive" class="notif-enable" @click="onEnablePush">
        🔔 {{ $t('notifications.enablePush') }}
      </button>

      <div v-if="pushError" class="push-error">{{ $t(`notifications.${pushError}`) }}</div>

      <div v-if="!unreadItems.length" class="notif-empty">{{ $t('notifications.empty') }}</div>

      <div
        v-for="n in unreadItems"
        :key="n.id"
        class="notif-item"
        :class="{ unread: !isRead(n), clickable: n.player_id }"
        @click="openFromNotification(n)"
      >
        <button
          class="read-toggle"
          :title="isRead(n) ? $t('notifications.markUnread') : $t('notifications.markRead')"
          :aria-pressed="isRead(n)"
          @click.stop="toggleRead(n)"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <template v-if="isRead(n)">
              <path d="M4 12.5l5 5L20 6.5" stroke-linecap="round" stroke-linejoin="round" />
            </template>
            <template v-else>
              <circle cx="12" cy="12" r="4.5" fill="currentColor" stroke="none" />
            </template>
          </svg>
        </button>

        <div class="ni-content">
          <div class="ni-title">{{ n.title }}</div>
          <div v-if="bodyOf(n)" class="ni-body">{{ bodyOf(n) }}</div>
          <div class="ni-time">{{ fmtDateTime(n.created_at) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Bezugspunkt für das Benachrichtigungsfeld: Es hing im Original am oberen
   linken Bildschirmrand, jetzt hängt es unter der Glocke — die sitzt in der
   festen Leiste und kann selbst verschoben werden. */
.notif-root {
  position: relative;
  display: inline-flex;
}
.notif-bell {
  display: flex;
}
.notif-panel-head .unread-hint {
  color: var(--green);
  font-size: var(--fs-xs);
  font-weight: 500;
}

/* Meldungen bekommen links einen Schalter für den Lesestatus. */
.notif-item {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.notif-item.clickable {
  cursor: pointer;
}
.ni-content {
  flex: 1;
  min-width: 0;
}
.read-toggle {
  flex: none;
  margin-top: 2px;
  padding: 3px;
  background: none;
  border: 0;
  border-radius: 50%;
  color: var(--dim);
  cursor: pointer;
  line-height: 0;
  transition: color 0.18s;
}
.read-toggle:hover {
  color: var(--green);
}
.notif-item.unread .read-toggle {
  color: var(--green);
}
/* Ungelesene heben sich durch einen Balken links ab. */
.notif-item.unread {
  border-left: 2px solid var(--green);
  background: var(--green-bg);
}
.notif-item:not(.unread) .ni-title {
  color: var(--muted);
  font-weight: 400;
}
.push-error {
  padding: 10px 14px;
  font-size: var(--fs-xs);
  color: var(--amber);
  line-height: 1.5;
}
</style>
